import { Loader, Loader2, PlusIcon, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { NarrativeSidebarElement } from "./NarrativeSidebarElement";
import useAdsPageStore, { Narrative } from "../../store/adsPageStore";
import { FilterAutocomplete } from "../filters/FilterAutocomplete";
import { useLoadNarratives } from "../../hooks/useLoadNarrativesWithFunnelParam";
import { useNarrativeScroll } from "../../hooks/useNarrativeScroll";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";

export interface Option {
  id: string;
  name: string;
}

export default function NarrativeSidebar() {
  const { language } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const {
    selectedNarrative,
    setSelectedNarrative,
    narratives,
    setNarratives,
    filteredNarratives,
    setFilteredNarratives,
  } = useAdsPageStore();
  const [productFilter, setProductFilter] = useState<Option | null>(null);
  const [angleFilter, setAngleFilter] = useState<Option | null>(null);
  const [loading, setLoading] = useState(false);
  const hasLoadedRef = useRef(false);
  const { loadNarrativesByFunnel } = useLoadNarratives(
    setNarratives,
    setSelectedNarrative,
    setProductFilter,
    setAngleFilter,
    setFilteredNarratives,
    setLoading
  );
  const funnelId = searchParams.get("funnelId");

  useEffect(() => {
    if (funnelId) return

    const filtered = narratives.filter((n) => {
      const matchesProduct = productFilter === null || n.product_information?.name?.toLowerCase().includes(productFilter.name.toLowerCase())
      
      const matchesAngle =
        !angleFilter ||
        n.sale_angle?.toLowerCase().includes(angleFilter.name.toLowerCase());

      return matchesProduct && matchesAngle;
    });

    setFilteredNarratives(filtered);
  }, [narratives, productFilter, angleFilter]);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadNarrativesByFunnel();
    }
  }, []);

  const { registerRef } = useNarrativeScroll(selectedNarrative);

  const productiteOptions: Option[] = Array.from(
    new Set(narratives.flatMap((n) => [n.product_information?.name]))
  )
    .filter((name): name is string => !!name)
    .map((name) => ({ id: name, name }));

  const angleOptions: Option[] = Array.from(
    new Set(narratives.map((n) => n.sale_angle))
  )
    .filter((name): name is string => !!name)
    .map((name) => ({ id: name, name }));

  const handleFilterProductOptions = (productFilter: Option | null) => {
    searchParams.delete("funnelId");
    searchParams.delete("sale_angle");
    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true }
    );
    setProductFilter(productFilter);
  };

  const handleFilterAngleOptions = (angleFilter: Option | null) => {
    searchParams.delete("funnelId");
    searchParams.delete("sale_angle");
    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString(),
      },
      { replace: true }
    );
    setAngleFilter(angleFilter);
  };

  return (
    <div className="w-80 border-r bg-white p-6 h-[100vh] flex flex-col">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="h-6 w-6 text-indigo-600" />
        <span className="text-xl font-bold text-gray-900 pt-5">
          {dictionary["your-ads"][language]}
        </span>
      </div>

      <div className="space-y-2 mb-4">
        <FilterAutocomplete
          label={dictionary["filter-by-product"][language]}
          options={productiteOptions}
          optionFilter={productFilter}
          onSelect={(selected) => handleFilterProductOptions(selected)}
          loading={false}
        />
        <FilterAutocomplete
          label={dictionary["filter-by-sale-angle"][language]}
          options={angleOptions}
          optionFilter={angleFilter}
          onSelect={(selected) => handleFilterAngleOptions(selected)}
          loading={false}
        />
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center flex-grow">
          <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <Loader2 size={40} color="#6569f1" className="animate-spin" />
          </div>
        </div>
      )}

      {narratives.length === 0 && !loading ? (
        <div className="flex flex-col items-center justify-center flex-grow">
          <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <PlusIcon className="h-6 w-6 text-slate-400" />
          </div>
          <div className="text-center">
            <h2 className="text-lg font-medium">{dictionary["no-narratives-yet"][language]}</h2>
            <p className="text-sm text-slate-500 mt-2">
              {dictionary["your-generated-narratives-will-appear-here"][language]}
              <br />
              {dictionary["start-filling-the-form-on-the-right"][language]}
            </p>
          </div>
        </div>
      ) : filteredNarratives.length === 0 && !loading ? (
        <div className="flex flex-col items-center justify-center flex-grow text-center text-slate-500">
          <p className="text-sm">
            {dictionary["no-narratives-found-for-this-filter"][language]}
          </p>
        </div>
      ) : null}
      {filteredNarratives.length > 0 && !loading && (
        <div className="space-y-4 overflow-y-auto h-[calc(100vh-250px)]">
          {filteredNarratives.map((narrative) => (
            <NarrativeSidebarElement
              key={narrative.id}
              narrative={narrative}
              selectedNarrative={selectedNarrative}
              setSelectedNarrative={setSelectedNarrative}
              ref={registerRef(narrative.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
